function analyticsConfig() {
	return browserWindow().fluxVars?.analytics || {};
}

function consentConfig() {
	return browserWindow().fluxVars?.cookieConsent || {};
}

function browserWindow() {
	return typeof window !== "undefined" ? window : {};
}

function browserDocument() {
	return typeof document !== "undefined" ? document : { cookie: "" };
}

function consentCookieName() {
	return consentConfig().cookieName || "flux_privacy_consent";
}

const DEFAULT_GOOGLE_TAG_EVENT_FORWARDING_EVENTS = [
	"view_item",
	"view_item_list",
	"select_item",
	"add_to_cart",
	"remove_from_cart",
	"view_cart",
	"mini_cart_open",
	"begin_checkout",
	"generate_lead",
	"live_chat_click",
	"mailto",
	"tel",
];

const GOOGLE_TAG_EVENT_PARAM_KEYS = new Set([
	"currency",
	"value",
	"coupon",
	"cart_surface",
	"form_id",
	"form_key",
	"form_title",
	"form_type",
	"lead_category",
	"lead_source",
	"chat_provider",
	"chat_surface",
	"page_path",
	"product_id",
	"product_sku",
	"item_list_id",
	"item_list_name",
	"items",
]);

const GOOGLE_TAG_ITEM_PARAM_KEYS = new Set([
	"item_id",
	"item_name",
	"item_brand",
	"item_category",
	"item_category2",
	"item_category3",
	"item_category4",
	"item_category5",
	"item_variant",
	"item_list_id",
	"item_list_name",
	"parent_item_id",
	"parent_item_name",
	"parent_item_sku",
	"product_sku",
	"variation_sku",
	"woocommerce_product_id",
	"woocommerce_variation_id",
	"coupon",
	"index",
	"price",
	"quantity",
]);

export function normalizeAnalyticsEventName(eventName) {
	return String(eventName || "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9_]/g, "_")
		.replace(/_+/g, "_")
		.replace(/^_|_$/g, "");
}

export function getCookieValue(cookieString, name) {
	const prefix = `${name}=`;
	return (
		String(cookieString || "")
			.split(";")
			.map((cookie) => cookie.trim())
			.find((cookie) => cookie.startsWith(prefix))
			?.slice(prefix.length) || ""
	);
}

export function readConsentFromCookie(cookieString, name) {
	const raw = getCookieValue(cookieString, name);
	if (!raw) {
		return null;
	}

	try {
		return JSON.parse(decodeURIComponent(raw));
	} catch (err) {
		return null;
	}
}

function readStoredConsent() {
	return readConsentFromCookie(browserDocument().cookie, consentCookieName());
}

export function consentAllowsCategory(consent, config, category) {
	if (category === "necessary") {
		return true;
	}

	if (!consent || typeof consent !== "object") {
		return false;
	}

	const expectedVersion = config?.version || "";
	if (expectedVersion && consent.version !== expectedVersion) {
		return false;
	}

	if (category === "advertising" && consent.gpc) {
		return false;
	}

	return Boolean(consent.categories?.[category]);
}

function consentAllows(category) {
	const win = browserWindow();
	if (win.fluxCookieConsent && typeof win.fluxCookieConsent.has === "function") {
		return win.fluxCookieConsent.has(category);
	}

	return consentAllowsCategory(readStoredConsent(), consentConfig(), category);
}

function googleTagForwardingEvents(config = googleTagConfig()) {
	const configuredEvents = Array.isArray(config.eventForwardingEvents)
		? config.eventForwardingEvents
		: DEFAULT_GOOGLE_TAG_EVENT_FORWARDING_EVENTS;

	return new Set(configuredEvents.map(normalizeAnalyticsEventName).filter(Boolean));
}

export function googleTagEventForwardingAllowed(eventName, config = googleTagConfig()) {
	const event = normalizeAnalyticsEventName(eventName);
	return Boolean(
		config.enabled &&
			config.eventForwardingEnabled !== false &&
			validGoogleTagId(config.tagId) &&
			googleTagForwardingEvents(config).has(event),
	);
}

function googleTagParamValue(value) {
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value === "boolean") {
		return value ? "1" : "0";
	}

	if (typeof value === "string") {
		const trimmed = value.trim();
		return trimmed === "" ? null : trimmed.slice(0, 2048);
	}

	return null;
}

function googleTagItemParams(item) {
	if (!item || typeof item !== "object" || Array.isArray(item)) {
		return null;
	}

	const sanitized = {};
	Object.entries(item).forEach(([key, value]) => {
		if (!GOOGLE_TAG_ITEM_PARAM_KEYS.has(key)) {
			return;
		}

		const paramValue = googleTagParamValue(value);
		if (paramValue !== null) {
			sanitized[key] = paramValue;
		}
	});

	return Object.keys(sanitized).length ? sanitized : null;
}

export function buildGoogleTagEventParams(params = {}) {
	if (!params || typeof params !== "object" || Array.isArray(params)) {
		return { transport_type: "beacon" };
	}

	const sanitized = {};
	Object.entries(params).forEach(([key, value]) => {
		if (!GOOGLE_TAG_EVENT_PARAM_KEYS.has(key)) {
			return;
		}

		if (key === "items") {
			const items = Array.isArray(value) ? value.map(googleTagItemParams).filter(Boolean) : [];
			if (items.length) {
				sanitized.items = items;
			}
			return;
		}

		const paramValue = googleTagParamValue(value);
		if (paramValue !== null) {
			sanitized[key] = paramValue;
		}
	});

	sanitized.transport_type = "beacon";
	return sanitized;
}

function forwardGoogleTagEvent(eventName, params = {}) {
	const config = googleTagConfig();
	const event = normalizeAnalyticsEventName(eventName);
	if (!googleTagEventForwardingAllowed(event, config)) {
		return { ok: false, skipped: "google_tag_event_not_allowed" };
	}

	loadGoogleTagIfAllowed();
	const win = browserWindow();
	if (typeof win.gtag !== "function") {
		return { ok: false, skipped: "google_tag_unavailable" };
	}

	win.gtag("event", event, buildGoogleTagEventParams(params));
	return { ok: true };
}

export function trackAnalyticsEvent(eventName, params = {}) {
	const config = analyticsConfig();
	const event = normalizeAnalyticsEventName(eventName);

	if (!config.enabled || !event) {
		return Promise.resolve({ ok: false, skipped: "disabled" });
	}

	const googleTagResult = forwardGoogleTagEvent(event, params);

	return Promise.resolve({
		ok: Boolean(googleTagResult.ok),
		...(googleTagResult.skipped ? { skipped: googleTagResult.skipped } : {}),
		...(googleTagResult.ok ? { destination: "google_tag" } : {}),
		googleTag: googleTagResult,
	});
}

const trackedCurrentEventKeys = new Set();
let consentUpdateListenerAttached = false;
let selectItemListenerAttached = false;
let removeFromCartListenerAttached = false;
let contactLinkListenerAttached = false;
let googleTagLoaded = false;
let googleTagConfigured = false;

function googleTagConfig() {
	return analyticsConfig().googleTag || {};
}

function googleConsentValue(allowed) {
	return allowed ? "granted" : "denied";
}

function advertisingConsentAllowed() {
	return consentAllows("advertising");
}

function updateGoogleTagConsent(advertisingAllowed = advertisingConsentAllowed()) {
	const win = browserWindow();
	if (typeof win.gtag !== "function") {
		return;
	}

	const advertisingStorage = googleConsentValue(advertisingAllowed);
	win.gtag("consent", googleTagConfigured ? "update" : "default", {
		analytics_storage: "granted",
		ad_storage: advertisingStorage,
		ad_user_data: advertisingStorage,
		ad_personalization: advertisingStorage,
	});
}

function validGoogleTagId(tagId) {
	return /^(G|GT|AW|DC)-[A-Z0-9_-]+$/i.test(String(tagId || ""));
}

function ensureGoogleTagGlobals() {
	const win = browserWindow();
	win.dataLayer = Array.isArray(win.dataLayer) ? win.dataLayer : [];
	if (typeof win.gtag !== "function") {
		win.gtag = function gtag() {
			win.dataLayer.push(arguments);
		};
	}
}

function loadGoogleTagScript(tagId) {
	if (googleTagLoaded) {
		return;
	}

	const doc = browserDocument();
	if (!doc.createElement || !doc.head?.appendChild) {
		return;
	}

	if (doc.querySelector?.(`script[data-flux-google-tag="${tagId}"]`)) {
		googleTagLoaded = true;
		return;
	}

	const script = doc.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
	script.dataset.fluxGoogleTag = tagId;
	doc.head.appendChild(script);
	googleTagLoaded = true;
}

function loadGoogleTagIfAllowed() {
	const config = googleTagConfig();
	const tagId = String(config.tagId || "").trim();
	const serverContainerUrl = String(config.serverContainerUrl || "").trim().replace(/\/+$/, "");

	if (!config.enabled || !validGoogleTagId(tagId)) {
		return;
	}

	ensureGoogleTagGlobals();
	updateGoogleTagConsent();
	if (!googleTagConfigured) {
		const configParams = {};
		if (serverContainerUrl) {
			configParams.server_container_url = serverContainerUrl;
		}

		browserWindow().gtag("js", new Date());
		browserWindow().gtag("config", tagId, configParams);
		googleTagConfigured = true;
	}
	loadGoogleTagScript(tagId);
}

function numberFromDataset(value) {
	const number = Number.parseFloat(value);
	return Number.isFinite(number) ? number : null;
}

function intFromDataset(value) {
	const number = Number.parseInt(value, 10);
	return Number.isFinite(number) && number >= 0 ? number : null;
}

function text(value) {
	return String(value || "").trim();
}

function numberFromText(value) {
	const normalized = String(value || "").replace(/[^0-9.-]/g, "");
	const number = Number.parseFloat(normalized);
	return Number.isFinite(number) ? number : null;
}

function productNameFromCard(card, source) {
	const fromSource = text(source?.dataset?.productName || source?.getAttribute?.("data-product-name"));
	if (fromSource) {
		return fromSource;
	}

	const title =
		card?.querySelector?.(".woocommerce-loop-product__title") ||
		card?.querySelector?.("h1, h2, h3, h4, h5, h6");

	return text(title?.textContent) || "Item";
}

function itemFromProductSource(source, index = 0) {
	if (!source) {
		return null;
	}

	const card = source.closest?.(".product") || source.closest?.("[data-ga4-product-card]");
	const productId = text(source.dataset?.product_id || source.dataset?.productId);
	const productSku = text(source.dataset?.product_sku || source.dataset?.productSku);
	const item = {
		item_id: productSku || productId,
		item_name: productNameFromCard(card, source),
		quantity: 1,
	};

	if (!item.item_id && !item.item_name) {
		return null;
	}

	if (productId) {
		item.woocommerce_product_id = productId;
	}
	if (productSku) {
		item.product_sku = productSku;
	}

	const price = numberFromDataset(source.dataset?.productPrice);
	if (price !== null) {
		item.price = price;
	}

	const category = text(source.dataset?.productCategory);
	if (category) {
		item.item_category = category;
	}

	const quantity = intFromDataset(source.dataset?.quantity);
	if (quantity !== null && quantity > 0) {
		item.quantity = quantity;
	}

	if (index > 0) {
		item.index = index;
	}

	return item;
}

function productSourceFromCard(card) {
	return card?.querySelector?.("[data-product_id], [data-product-id], [data-product_sku], [data-product-sku]");
}

function listContextFromElement(element) {
	const list = element?.closest?.("[data-ga4-item-list-name], .flux-linked-products, .products");
	const doc = browserDocument();
	const body = doc.body;

	let itemListId = text(list?.getAttribute?.("data-ga4-item-list-id"));
	let itemListName = text(list?.getAttribute?.("data-ga4-item-list-name"));

	if (!itemListName && element?.closest?.(".flux-linked-products")) {
		itemListName = "Linked Products";
		itemListId = itemListId || "linked_products";
	}

	if (!itemListName && body?.classList?.contains("search")) {
		itemListName = "Search Results";
		itemListId = itemListId || "search_results";
	}

	if (!itemListName) {
		const heading = doc.querySelector(".woocommerce-products-header__title, h1");
		itemListName = text(heading?.textContent) || "Product Listing";
	}
	if (!itemListId) {
		itemListId = normalizeAnalyticsEventName(itemListName);
	}

	return {
		item_list_id: itemListId,
		item_list_name: itemListName,
	};
}

export function buildProductListEvent(doc = browserDocument()) {
	const cards = Array.from(
		doc.querySelectorAll("[data-ga4-product-list] .product, .products .product, .flux-linked-products .product"),
	);
	const seen = new Set();
	const items = [];
	let context = null;

	for (const card of cards) {
		const source = productSourceFromCard(card);
		const item = itemFromProductSource(source, items.length + 1);
		if (!item?.item_id || seen.has(item.item_id)) {
			continue;
		}

		seen.add(item.item_id);
		items.push(item);
		if (!context) {
			context = listContextFromElement(card);
		}
		if (items.length >= 50) {
			break;
		}
	}

	if (!items.length) {
		return null;
	}

	return {
		event: "view_item_list",
		params: {
			...(context || {}),
			items,
		},
	};
}

function buildSelectItemEvent(link) {
	const card = link?.closest?.(".product");
	if (!card) {
		return null;
	}

	if (link.closest(".add_to_cart_button, .ajax_add_to_cart")) {
		return null;
	}

	const source = productSourceFromCard(card);
	const item = itemFromProductSource(source);
	if (!item?.item_id) {
		return null;
	}

	const context = listContextFromElement(card);
	item.item_list_id = context.item_list_id;
	item.item_list_name = context.item_list_name;

	return {
		event: "select_item",
		params: {
			...context,
			items: [item],
		},
	};
}

function cartSurfaceFromElement(element, doc = browserDocument()) {
	if (element?.closest?.("[data-mini-cart], .flux-mini-cart")) {
		return "mini_cart";
	}
	if (
		doc.body?.classList?.contains("woocommerce-checkout") ||
		element?.closest?.(".woocommerce-checkout-review-order, form.checkout")
	) {
		return "checkout";
	}
	if (doc.body?.classList?.contains("woocommerce-cart") || element?.closest?.(".woocommerce-cart-form")) {
		return "cart";
	}

	return "cart";
}

function productNameFromRemoveLink(removeLink, row) {
	const explicit = text(removeLink?.dataset?.productName || removeLink?.getAttribute?.("data-product-name"));
	if (explicit) {
		return explicit;
	}

	const nameNode =
		row?.querySelector?.(".product-name a") ||
		row?.querySelector?.(".product-name") ||
		row?.querySelector?.(".wc-block-components-product-name") ||
		row?.querySelector?.("a:not(.remove)");
	const name = text(nameNode?.textContent);
	if (name) {
		return name;
	}

	const label = text(removeLink?.getAttribute?.("aria-label") || removeLink?.getAttribute?.("title"));
	const match = label.match(/remove\s+(.+?)(?:\s+from\s+(?:cart|basket))?$/i);
	return text(match?.[1]) || "Item";
}

function quantityFromCartRow(row) {
	const input = row?.querySelector?.(
		"input.qty, input[name*='cart'][name*='qty'], .wc-block-components-quantity-selector input, input[aria-label*='Quantity']",
	);
	const inputQuantity = intFromDataset(input?.value || input?.getAttribute?.("value"));
	if (inputQuantity !== null && inputQuantity > 0) {
		return inputQuantity;
	}

	const quantityText = text(
		row?.querySelector?.(".product-quantity, .wc-block-components-product-metadata")?.textContent,
	);
	const match = quantityText.match(/(?:qty|quantity)?\s*[:x]?\s*(\d+)/i);
	const textQuantity = intFromDataset(match?.[1]);
	return textQuantity !== null && textQuantity > 0 ? textQuantity : 1;
}

function priceFromCartRow(row, quantity) {
	const subtotal = numberFromText(
		row?.querySelector?.(".product-subtotal .amount, .wc-block-cart-item__total .amount")?.textContent,
	);
	if (subtotal !== null && quantity > 0) {
		return subtotal / quantity;
	}

	return numberFromText(
		row?.querySelector?.(".product-price .amount, .wc-block-components-product-price__value, .amount")?.textContent,
	);
}

export function buildRemoveFromCartEvent(removeLink, doc = browserDocument()) {
	if (!removeLink || removeLink.closest?.("[data-mini-cart], .flux-mini-cart")) {
		return null;
	}

	const row = removeLink.closest?.(
		".woocommerce-cart-form__cart-item, tr.cart_item, .cart_item, .wc-block-cart-items__row",
	);
	const productId = text(removeLink.dataset?.product_id || removeLink.dataset?.productId);
	const productSku = text(removeLink.dataset?.product_sku || removeLink.dataset?.productSku);
	const quantity = quantityFromCartRow(row);
	const item = {
		item_id: productSku || productId || productNameFromRemoveLink(removeLink, row),
		item_name: productNameFromRemoveLink(removeLink, row),
		quantity,
	};

	if (productId) {
		item.woocommerce_product_id = productId;
	}
	if (productSku) {
		item.product_sku = productSku;
	}

	const price = priceFromCartRow(row, quantity);
	if (price !== null) {
		item.price = price;
	}

	const params = {
		cart_surface: cartSurfaceFromElement(removeLink, doc),
		currency: analyticsConfig().currency || "USD",
		items: [item],
	};

	if (price !== null) {
		params.value = price * quantity;
	}

	return {
		event: "remove_from_cart",
		params,
	};
}

function currentPageEvents() {
	const events = [];
	const config = analyticsConfig();

	const configuredEvents = Array.isArray(config.currentEvents)
		? config.currentEvents
		: config.currentEvent
			? [config.currentEvent]
			: [];

	configuredEvents.forEach((event, index) => {
		if (event?.event && event?.params) {
			events.push({
				event: event.event,
				params: event.params,
				key: `${event.event}:${index}`,
			});
		}
	});

	const productListEvent = buildProductListEvent();
	if (productListEvent) {
		events.push({
			...productListEvent,
			key: "view_item_list",
		});
	}

	return events;
}

function contactLinkEvent(link, doc = browserDocument()) {
	const href = text(link?.getAttribute?.("href"));
	const scheme = href.split(":", 1)[0].toLowerCase();
	if (scheme !== "mailto" && scheme !== "tel") {
		return null;
	}

	const pagePath = doc.location?.pathname || browserWindow().location?.pathname || "";
	return {
		event: scheme,
		params: {
			...(pagePath ? { page_path: pagePath } : {}),
		},
	};
}

function trackCurrentPageEvent() {
	currentPageEvents().forEach((event) => {
		const key = event.key || event.event;
		if (trackedCurrentEventKeys.has(key)) {
			return;
		}

		trackAnalyticsEvent(event.event, event.params).then((result) => {
			if (result?.ok) {
				trackedCurrentEventKeys.add(key);
			}
		});
	});
}

function bindConsentUpdateTracking() {
	if (consentUpdateListenerAttached) {
		return;
	}

	consentUpdateListenerAttached = true;
	browserDocument().addEventListener?.("flux:cookie-consent-updated", (event) => {
		updateGoogleTagConsent(consentAllowsCategory(event?.detail?.consent, consentConfig(), "advertising"));
		loadGoogleTagIfAllowed();
	});
}

function bindSelectItemTracking() {
	if (selectItemListenerAttached) {
		return;
	}

	selectItemListenerAttached = true;
	browserDocument().addEventListener?.("click", (event) => {
		const link = event.target?.closest?.("a[href]");
		if (!link) {
			return;
		}

		const selectEvent = buildSelectItemEvent(link);
		if (!selectEvent) {
			return;
		}

		trackAnalyticsEvent(selectEvent.event, selectEvent.params, { keepalive: true });
	});
}

function bindRemoveFromCartTracking() {
	if (removeFromCartListenerAttached) {
		return;
	}

	removeFromCartListenerAttached = true;
	browserDocument().addEventListener?.(
		"click",
		(event) => {
			const removeLink = event.target?.closest?.(
				"a.remove[href], a.remove_from_cart_button[href], button.wc-block-cart-item__remove-link, button[aria-label^='Remove '], [data-cart-remove]",
			);
			if (!removeLink) {
				return;
			}

			const removeEvent = buildRemoveFromCartEvent(removeLink);
			if (!removeEvent) {
				return;
			}

			trackAnalyticsEvent(removeEvent.event, removeEvent.params, { keepalive: true });
		},
		true,
	);
}

function bindContactLinkTracking() {
	if (contactLinkListenerAttached) {
		return;
	}

	contactLinkListenerAttached = true;
	browserDocument().addEventListener?.("click", (event) => {
		const link = event.target?.closest?.('a[href^="mailto:"], a[href^="tel:"]');
		const contactEvent = contactLinkEvent(link);
		if (!contactEvent) {
			return;
		}

		trackAnalyticsEvent(contactEvent.event, contactEvent.params, { keepalive: true });
	});
}

export function ga4ServerEvents() {
	const win = browserWindow();
	if (!win.fluxTrackAnalyticsEvent) {
		win.fluxTrackAnalyticsEvent = trackAnalyticsEvent;
	}

	bindConsentUpdateTracking();
	bindSelectItemTracking();
	bindRemoveFromCartTracking();
	bindContactLinkTracking();
	loadGoogleTagIfAllowed();
	trackCurrentPageEvent();
}
